import { ConsoleContinue } from "components/platform/companyConsole/ConsoleState";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { useCompanyConnection } from "hooks/useCompanyConnection";
import { ConsoleError } from "components/platform/companyConsole/ConsoleState";
import dayjs from "utils/dayjs";
import { Alert, Box, Button, Typography } from "@mui/material";
import { SummaryItem } from "components/common/Summary";
import { GET_PLATFORM_EVENT_LOGS } from "graphql/eventLogs";
import { GET_PLATFORM_WEBHOOK_ENDPOINTS } from "graphql/webhookDeliveries";
import { Card, Drawer, PageContainer, ScrollArea, Select } from "@outpave/ds";
import Summary from "components/common/Summary";
import { Stack } from "components/layout/reusableLayouts";
import { DataTable } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import {
  buildGenericItems,
  buildImpersonationItems,
  extractEventLogRows,
  formatActor,
  serviceLabelKey,
  type EventLogRow,
  type EventLogNode,
} from "./eventLogsUtils";
import WebhookDeliveries from "./WebhookDeliveries";
import {
  getEndpointLabel,
  type PlatformWebhookEndpoint,
} from "./webhookDeliveriesUtils";

const SERVICES = [
  "ALL",
  "IMPERSONATION",
  "ACCOUNTS",
  "USERS",
  "PLATFORM",
  "REWARDS",
  "API_KEYS",
  "WEBHOOKS",
] as const;
type ServiceFilter = (typeof SERVICES)[number];
type EventLogsTab = "activity" | "webhooks";

interface EventLogsProps {
  selectedCompanyId?: string;
  embedded?: boolean;
  view?: EventLogsTab;
  eventId?: string;
  resourceId?: string;
  startDate?: number;
  endDate?: number;
}
const EventLogs: React.FC<EventLogsProps> = ({
  selectedCompanyId,
  embedded = false,
  view,
  eventId,
  resourceId,
  startDate,
  endDate,
}) => {
  const { t } = useTranslation();
  const [ownTab, setTab] = useState<EventLogsTab>("activity");
  const tab = view ?? ownTab;
  const [serviceFilter, setServiceFilter] = useState<ServiceFilter>("ALL");
  const [selectedLog, setSelectedLog] = useState<EventLogRow | null>(null);
  const [endpointId, setEndpointId] = useState<string | null>(null);

  const endpointsQuery = useQuery<{
    platformWebhookEndpoints: PlatformWebhookEndpoint[];
  }>(GET_PLATFORM_WEBHOOK_ENDPOINTS, {
    skip: tab !== "webhooks",
    variables: { selectedCompanyId },
    fetchPolicy: "no-cache",
  });
  const endpoints = useMemo(
    () => endpointsQuery.data?.platformWebhookEndpoints ?? [],
    [endpointsQuery.data],
  );
  const activeEndpointId =
    endpoints.find((endpoint) => endpoint.id === endpointId)?.id ??
    endpoints[0]?.id ??
    null;

  const initialVariables = useMemo(
    () => ({
      service: serviceFilter === "ALL" ? undefined : serviceFilter,
      first: 25,
      after: undefined,
    }),
    [serviceFilter],
  );

  const { dataArray, displayLoading, handleScroll } = useInfiniteScroll({
    query: GET_PLATFORM_EVENT_LOGS,
    initialVariables,
    skip: tab !== "activity" || !!selectedCompanyId,
    extractData: extractEventLogRows,
  });

  const scoped = useCompanyConnection<EventLogNode>({
    query: GET_PLATFORM_EVENT_LOGS,
    variables: {
      ...initialVariables,
      selectedCompanyId,
      resourceId,
      startDate,
      endDate,
    },
    field: "platformEventLogs",
    skip: !selectedCompanyId || tab !== "activity",
  });
  const rows: EventLogRow[] = selectedCompanyId
    ? extractEventLogRows({
        platformEventLogs: { edges: scoped.rows.map((node) => ({ node })) },
      })
    : (dataArray as EventLogRow[]);

  const columns: DataColumn<EventLogRow>[] = useMemo(
    () => [
      {
        id: "when",
        header: t("When"),
        cell: (row) =>
          row.occurredAt
            ? dayjs(row.occurredAt).format("YYYY/MM/DD HHmmss")
            : "-",
      },
      {
        id: "service",
        header: t("Service"),
        cell: (row) => t(serviceLabelKey(row.service)),
      },
      {
        id: "action",
        header: t("Action"),
        cell: (row) => (
          <Typography variant="body2" noWrap>
            {row.label}
          </Typography>
        ),
      },
      {
        id: "actor",
        header: t("Actor"),
        cell: (row) => (
          <Typography variant="body2" noWrap>
            {formatActor(row.actor)}
          </Typography>
        ),
      },
    ],
    [t],
  );

  // Matches the "Account: All" / "Acme Corp" pattern used by the Platform
  // Users page (AccountSearchAutoComplete) so the default shows the filter
  // dimension; chosen values show plain.
  const serviceLabel = (s: ServiceFilter) =>
    s === "ALL" ? t("ServiceAll") : t(serviceLabelKey(s));

  const drawerTitle = selectedLog
    ? t(serviceLabelKey(selectedLog.service))
    : "";

  const detailItems: SummaryItem[] = useMemo(() => {
    if (!selectedLog) return [];
    return selectedLog.service === "IMPERSONATION"
      ? buildImpersonationItems(
          selectedLog.parsed,
          selectedLog.occurredAt,
          selectedLog.actor,
          selectedLog.label,
          t,
        )
      : buildGenericItems(
          selectedLog.parsed,
          selectedLog.occurredAt,
          selectedLog.actor,
          selectedLog.label,
          t,
        );
  }, [selectedLog, t]);

  const filters =
    tab === "activity" ? (
      <Select
        options={SERVICES.map((s) => ({ id: s, label: serviceLabel(s) }))}
        value={serviceFilter}
        onChange={(id) => id && setServiceFilter(id as ServiceFilter)}
        placeholder={t("Service")}
        aria-label={t("Service")}
        // "ALL" is already an option, so the field is never empty; required
        // is how the DS suppresses a clear that would only snap back.
        required
        size="md"
        width={360}
      />
    ) : endpoints.length > 0 ? (
      <Select
        options={endpoints.map((endpoint) => ({
          id: endpoint.id,
          label: getEndpointLabel(endpoint, t),
        }))}
        value={activeEndpointId}
        onChange={(id) => id && setEndpointId(id)}
        placeholder={t("Webhook endpoint")}
        aria-label={t("Webhook endpoint")}
        required
        size="md"
        width={360}
      />
    ) : undefined;

  const content = (
    <>
      {scoped.error && <ConsoleError retry={scoped.retry} />}
      <ConsoleContinue paused={scoped.paused} loadMore={scoped.loadMore} />
      {tab === "webhooks" && endpointsQuery.error && (
        <ConsoleError retry={() => void endpointsQuery.refetch()} />
      )}
      {tab === "activity" ? (
        <>
          <DataTable<EventLogRow>
            columns={columns}
            rows={rows}
            getRowKey={(row) => row.id}
            loading={selectedCompanyId ? scoped.loading : displayLoading}
            isFetchingMore={selectedCompanyId ? scoped.isFetchingMore : false}
            onRowClick={(row) => setSelectedLog(row)}
            onScroll={selectedCompanyId ? scoped.handleScroll : handleScroll}
            emptyState={t("No event logs found")}
            stickyHeader
          />

          <Drawer
            open={Boolean(selectedLog)}
            onClose={() => setSelectedLog(null)}
            title={[drawerTitle]}
            footer={
              <Button
                variant="default-outlined"
                onClick={() => setSelectedLog(null)}
              >
                {t("Exit")}
              </Button>
            }
          >
            <Summary items={detailItems} disableContainer />
          </Drawer>
        </>
      ) : embedded && endpoints.length > 1 ? (
        <ScrollArea sx={{ flex: 1, minHeight: 0 }}>
          <Stack gap="var(--space-4)">
            {endpoints.map((endpoint) => (
              <Card key={endpoint.id} title={getEndpointLabel(endpoint, t)}>
                <Box sx={{ height: 340, minHeight: 0 }}>
                  <WebhookDeliveries
                    selectedCompanyId={selectedCompanyId}
                    eventId={eventId}
                    resourceId={resourceId}
                    startDate={startDate}
                    endDate={endDate}
                    endpointId={endpoint.id}
                    endpointsLoading={endpointsQuery.loading}
                  />
                </Box>
              </Card>
            ))}
          </Stack>
        </ScrollArea>
      ) : (
        <WebhookDeliveries
          selectedCompanyId={selectedCompanyId}
          eventId={eventId}
          resourceId={resourceId}
          startDate={startDate}
          endDate={endDate}
          endpointId={activeEndpointId}
          endpointsLoading={endpointsQuery.loading}
        />
      )}
    </>
  );
  if (embedded)
    return (
      <Box
        ref={scoped.viewportRef}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
        }}
      >
        {tab === "activity" && (
          <Alert severity="info">
            {t(
              "Older audit records without account attribution are unavailable here",
            )}
          </Alert>
        )}
        {content}
      </Box>
    );
  return (
    <PageContainer
      title={t("Event Logs")}
      tabs={{
        items: [
          { id: "activity", label: t("Activity") },
          { id: "webhooks", label: t("Webhook deliveries") },
        ],
        activeId: tab,
        onChange: (id) => setTab(id as EventLogsTab),
      }}
      filters={filters}
    >
      {content}
    </PageContainer>
  );
};

export default EventLogs;
